/**
 * Serialização dos filtros de pesquisa de/para a URL (deep-link).
 *
 * A URL é a fonte da verdade da pesquisa: abrir uma URL com filtros reproduz a
 * busca (é assim que "compartilhar" funciona). `page` e `pageSize` são sempre
 * gravados quando há uma pesquisa aplicada — a presença de qualquer parâmetro
 * distingue "pesquisa aplicada" do estado inicial vazio.
 */

import type { SearchFilters } from "@/types/api";

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

/** Profundidade máxima de paginação suportada pela API (offset + limite). */
export const MAX_RESULT_DEPTH = 10_000;

export interface SearchState {
  filters: SearchFilters;
  page: number;
  pageSize: number;
}

function parseIntList(value: string | null): number[] {
  if (!value) {
    return [];
  }
  const out: number[] = [];
  for (const part of value.split(",")) {
    const n = Number.parseInt(part.trim(), 10);
    if (Number.isFinite(n)) {
      out.push(n);
    }
  }
  return out;
}

function parseStrList(value: string | null): string[] {
  if (!value) {
    return [];
  }
  return value
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function parseNum(value: string | null): number | null {
  if (value === null || value.trim() === "") {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Extrai os filtros a partir dos parâmetros de busca da URL. */
export function parseFilters(params: URLSearchParams): SearchFilters {
  const filters: SearchFilters = {};

  const termo = params.get("termo")?.trim();
  if (termo) {
    filters.termo = termo;
  }

  const cnpj = params.get("cnpj")?.replace(/\D/g, "");
  if (cnpj && cnpj.length === 14) {
    filters.cnpj = cnpj;
  }
  const cnpjBasico = params.get("cnpjBasico")?.replace(/\D/g, "");
  if (cnpjBasico && cnpjBasico.length === 8) {
    filters.cnpjBasico = cnpjBasico;
  }

  const cnae = parseIntList(params.get("cnae"));
  if (cnae.length > 0) {
    filters.cnae = cnae;
  }
  if (params.get("incluirCnaeSecundaria") === "1") {
    filters.incluirCnaeSecundaria = true;
  }

  const natureza = parseIntList(params.get("natureza"));
  if (natureza.length > 0) {
    filters.natureza = natureza;
  }

  const situacao = parseIntList(params.get("situacao"));
  if (situacao.length > 0) {
    filters.situacao = situacao;
  }

  const uf = parseStrList(params.get("uf")).map((v) => v.toUpperCase());
  if (uf.length > 0) {
    filters.uf = uf;
  }

  const municipio = parseIntList(params.get("municipio"));
  if (municipio.length > 0) {
    filters.municipio = municipio;
  }

  const bairro = params.get("bairro")?.trim();
  if (bairro) {
    filters.bairro = bairro;
  }

  const cep = params.get("cep")?.replace(/\D/g, "");
  if (cep) {
    filters.cep = cep;
  }

  const ddd = params.get("ddd")?.replace(/\D/g, "");
  if (ddd) {
    filters.ddd = ddd;
  }

  const aberturaDe = params.get("aberturaDe")?.trim();
  if (aberturaDe) {
    filters.aberturaDe = aberturaDe;
  }
  const aberturaAte = params.get("aberturaAte")?.trim();
  if (aberturaAte) {
    filters.aberturaAte = aberturaAte;
  }

  const capitalDe = parseNum(params.get("capitalDe"));
  if (capitalDe !== null) {
    filters.capitalDe = capitalDe;
  }
  const capitalAte = parseNum(params.get("capitalAte"));
  if (capitalAte !== null) {
    filters.capitalAte = capitalAte;
  }

  const porte = parseIntList(params.get("porte"));
  if (porte.length > 0) {
    filters.porte = porte;
  }

  if (params.get("somenteMei") === "1") {
    filters.somenteMei = true;
  }
  if (params.get("excluirMei") === "1") {
    filters.excluirMei = true;
  }
  if (params.get("somenteMatriz") === "1") {
    filters.somenteMatriz = true;
  }
  if (params.get("somenteFilial") === "1") {
    filters.somenteFilial = true;
  }
  if (params.get("comTelefone") === "1") {
    filters.comTelefone = true;
  }
  if (params.get("somenteFixo") === "1") {
    filters.somenteFixo = true;
  }
  if (params.get("somenteCelular") === "1") {
    filters.somenteCelular = true;
  }
  if (params.get("comEmail") === "1") {
    filters.comEmail = true;
  }

  return filters;
}

/** Normaliza o tamanho de página para uma das opções suportadas. */
export function normalizePageSize(value: number | null | undefined): number {
  const allowed = PAGE_SIZE_OPTIONS as readonly number[];
  if (value != null && allowed.includes(value)) {
    return value;
  }
  return DEFAULT_PAGE_SIZE;
}

/** Extrai o estado completo da pesquisa (filtros + paginação) da URL. */
export function parseSearchState(params: URLSearchParams): SearchState {
  const page = parseNum(params.get("page"));
  return {
    filters: parseFilters(params),
    page: page != null && page >= 1 ? Math.floor(page) : 1,
    pageSize: normalizePageSize(parseNum(params.get("pageSize"))),
  };
}

/** Escreve apenas os filtros ativos em um `URLSearchParams`. */
function writeFilters(params: URLSearchParams, filters: SearchFilters): void {
  if (filters.termo) {
    params.set("termo", filters.termo);
  }
  if (filters.cnpj) {
    params.set("cnpj", filters.cnpj);
  }
  if (filters.cnpjBasico) {
    params.set("cnpjBasico", filters.cnpjBasico);
  }
  if (filters.cnae?.length) {
    params.set("cnae", filters.cnae.join(","));
  }
  if (filters.incluirCnaeSecundaria) {
    params.set("incluirCnaeSecundaria", "1");
  }
  if (filters.natureza?.length) {
    params.set("natureza", filters.natureza.join(","));
  }
  if (filters.situacao?.length) {
    params.set("situacao", filters.situacao.join(","));
  }
  if (filters.uf?.length) {
    params.set("uf", filters.uf.join(","));
  }
  if (filters.municipio?.length) {
    params.set("municipio", filters.municipio.join(","));
  }
  if (filters.bairro) {
    params.set("bairro", filters.bairro);
  }
  if (filters.cep) {
    params.set("cep", filters.cep);
  }
  if (filters.ddd) {
    params.set("ddd", filters.ddd);
  }
  if (filters.aberturaDe) {
    params.set("aberturaDe", filters.aberturaDe);
  }
  if (filters.aberturaAte) {
    params.set("aberturaAte", filters.aberturaAte);
  }
  if (filters.capitalDe !== undefined) {
    params.set("capitalDe", String(filters.capitalDe));
  }
  if (filters.capitalAte !== undefined) {
    params.set("capitalAte", String(filters.capitalAte));
  }
  if (filters.porte?.length) {
    params.set("porte", filters.porte.join(","));
  }
  if (filters.somenteMei) {
    params.set("somenteMei", "1");
  }
  if (filters.excluirMei) {
    params.set("excluirMei", "1");
  }
  if (filters.somenteMatriz) {
    params.set("somenteMatriz", "1");
  }
  if (filters.somenteFilial) {
    params.set("somenteFilial", "1");
  }
  if (filters.comTelefone) {
    params.set("comTelefone", "1");
  }
  if (filters.somenteFixo) {
    params.set("somenteFixo", "1");
  }
  if (filters.somenteCelular) {
    params.set("somenteCelular", "1");
  }
  if (filters.comEmail) {
    params.set("comEmail", "1");
  }
}

/**
 * Monta os parâmetros da URL para uma pesquisa aplicada — inclui sempre `page`
 * e `pageSize`, para que a pesquisa seja detectável mesmo sem nenhum filtro.
 */
export function buildSearchParams(
  filters: SearchFilters,
  page: number,
  pageSize: number,
): URLSearchParams {
  const params = new URLSearchParams();
  writeFilters(params, filters);
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  return params;
}

/**
 * Serializa apenas os filtros (sem paginação) — usado como chave estável para
 * detectar mudanças na parte de filtros da URL, ignorando `page`/`pageSize`.
 */
export function serializeFiltersOnly(filters: SearchFilters): string {
  const params = new URLSearchParams();
  writeFilters(params, filters);
  return params.toString();
}

/** Indica se há ao menos um filtro ativo (ignora paginação). */
export function hasActiveFilters(filters: SearchFilters): boolean {
  const params = new URLSearchParams();
  writeFilters(params, filters);
  return params.toString().length > 0;
}

/** Conta quantos filtros estão ativos (para exibir um contador). */
export function countActiveFilters(filters: SearchFilters): number {
  const params = new URLSearchParams();
  writeFilters(params, filters);
  return Array.from(params.keys()).length;
}
